import VectorLayer from "ol/layer/Vector";
import VectorSource from "ol/source/Vector";
import GeoJSON from "ol/format/GeoJSON";
import { transformExtent } from "ol/proj";
import { getMomaTracesStyleNormal } from "./style/momaTraces";

export const MOMA_MIN_ZOOM = 16;
const DEBOUNCE_MS = 400;

// Refetch only when the view leaves the last-fetched bbox by more than this
// fraction of its size (padding fetched around the viewport covers small pans).
const FETCH_PAD = 0.25;

// Server-side bbox guard is 5 km diagonal; clamp our request just under it so
// wide viewports at the minimum zoom still fetch (the centre of the view wins).
const MAX_REQUEST_DIAG_M = 4800;

function paddedViewBbox(map) {
  const extent3857 = map.getView().calculateExtent(map.getSize());
  const [west, south, east, north] = transformExtent(
    extent3857,
    "EPSG:3857",
    "EPSG:4326",
  );
  const padLon = (east - west) * FETCH_PAD;
  const padLat = (north - south) * FETCH_PAD;
  const bbox = {
    west: west - padLon,
    south: south - padLat,
    east: east + padLon,
    north: north + padLat,
  };
  const mLat = 111320;
  const mLon = 111320 * Math.cos((((south + north) / 2) * Math.PI) / 180);
  const diag = Math.hypot(
    (bbox.east - bbox.west) * mLon,
    (bbox.north - bbox.south) * mLat,
  );
  if (diag > MAX_REQUEST_DIAG_M) {
    const shrink = MAX_REQUEST_DIAG_M / diag;
    const cLon = (bbox.west + bbox.east) / 2;
    const cLat = (bbox.south + bbox.north) / 2;
    const halfLon = ((bbox.east - bbox.west) / 2) * shrink;
    const halfLat = ((bbox.north - bbox.south) / 2) * shrink;
    return {
      west: cLon - halfLon,
      south: cLat - halfLat,
      east: cLon + halfLon,
      north: cLat + halfLat,
    };
  }
  return bbox;
}

function bboxContains(outer, inner) {
  return (
    outer &&
    inner.west >= outer.west &&
    inner.east <= outer.east &&
    inner.south >= outer.south &&
    inner.north <= outer.north
  );
}

export function getMomaTracesLayer(map, momaTracesControl, momaTracesUrl) {
  const source = new VectorSource();
  const layer = new VectorLayer({
    source,
    style: getMomaTracesStyleNormal,
    visible: false,
    zIndex: 12,
  });

  const format = new GeoJSON({
    dataProjection: "EPSG:4326",
    featureProjection: "EPSG:3857",
  });

  let debounceTimer = null;
  let inFlight = null;
  let fetchedBbox = null;

  async function fetchTraces() {
    const bbox = paddedViewBbox(map);
    if (inFlight) inFlight.abort();
    const aborter = new AbortController();
    inFlight = aborter;
    momaTracesControl.set("momaStatus", "loading…");
    try {
      const query =
        `bbox=${bbox.west.toFixed(6)},${bbox.south.toFixed(6)},` +
        `${bbox.east.toFixed(6)},${bbox.north.toFixed(6)}`;
      const response = await fetch(`${momaTracesUrl}?${query}`, {
        signal: aborter.signal,
      });
      if (!response.ok) {
        const detail =
          response.status === 400
            ? "area too large"
            : `HTTP ${response.status}`;
        momaTracesControl.set("momaStatus", `failed (${detail})`);
        return;
      }
      const body = await response.json();
      source.clear();
      source.addFeatures(format.readFeatures(body));
      fetchedBbox = bbox;
      const props = body.properties || {};
      momaTracesControl.set(
        "momaStatus",
        `${props.sessionsLoaded ?? "?"} sessions` +
          (props.truncated ? ` of ${props.sessionsFound} (newest first)` : ""),
      );
    } catch (error) {
      if (error.name !== "AbortError") {
        momaTracesControl.set("momaStatus", "failed (network)");
      }
    } finally {
      if (inFlight === aborter) inFlight = null;
    }
  }

  function onViewChange() {
    const zoom = map.getView().getZoom();
    momaTracesControl.set("zoom", zoom);
    if (!momaTracesControl.get("momaToggle")) return;
    if (zoom < MOMA_MIN_ZOOM) {
      momaTracesControl.set("momaStatus", `zoom to ≥ ${MOMA_MIN_ZOOM}`);
      return;
    }
    const view = paddedViewBbox(map);
    // shrink the pad back to the raw viewport for the containment check
    const shrink = FETCH_PAD / (1 + 2 * FETCH_PAD);
    const raw = {
      west: view.west + (view.east - view.west) * shrink,
      east: view.east - (view.east - view.west) * shrink,
      south: view.south + (view.north - view.south) * shrink,
      north: view.north - (view.north - view.south) * shrink,
    };
    if (bboxContains(fetchedBbox, raw)) return;
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(fetchTraces, DEBOUNCE_MS);
  }

  map.on("moveend", onViewChange);

  momaTracesControl.on("propertychange", function (e) {
    if (e.key !== "momaToggle") return;
    const on = momaTracesControl.get("momaToggle");
    layer.setVisible(on);
    if (on) {
      fetchedBbox = null;
      onViewChange();
    } else {
      clearTimeout(debounceTimer);
      if (inFlight) inFlight.abort();
      source.clear();
      fetchedBbox = null;
      momaTracesControl.set("momaStatus", "");
    }
  });

  return layer;
}
